/**
 * AudioFrame + MouthSample -> GestureEvent.
 *
 * Seven sounds, checked in SPEC1.md's order — closures and transients before
 * sustained states, because a plosive is over before a fricative has begun to
 * qualify, and a classifier that checks the slow things first will call the
 * burst at the start of "pff" a fricative.
 *
 * ```
 * attack>15dB && dur<80ms && flatness>0.5              -> plosive
 * trill peak present                                    -> trill
 * f0 in 800..3000 && periodicity>0.85 && flatness<0.2   -> whistle
 * f0<80 && periodicity<0.5 && rms>thresh                -> growl
 * zcr>0.6 && flatness>0.5 && dur>150ms                  -> fricative
 * attack>20dB && dur<40ms && rms low tail               -> click
 * flatness>0.7 && f0 absent && dur>200ms                -> raspberry
 * ```
 *
 * ### Hysteresis in milliseconds, not frames
 *
 * The spec says two consecutive analysis hops. Its hops are the envelope's,
 * 85 ms apart; these frames arrive on rAF, which has been anywhere between 13
 * and 60 Hz on this project's own recordings depending on how loaded the main
 * thread was. Two frames is 33 ms on a good day and 154 ms on a bad one, so a
 * frame count makes the classifier's behaviour a property of the machine.
 * `HYSTERESIS_MS` is the same idea expressed in something that does not move.
 *
 * ### Vision is load-bearing, and not where the spec put it
 *
 * SPEC1.md offers vision as a confidence adjustment for plosives: a preceding
 * `mouthClose` peak takes confidence from 0.6 to 1.0. Two things about that did
 * not survive contact with a real face.
 *
 * **`mouthClose` does not fire.** Over a take containing four "papa"s and four
 * "meep"s it peaked at 0.044 and sat at 0.001 throughout. It is an ARKit
 * corrective meaning "lips together *while the jaw is open*", and during a
 * bilabial closure the jaw is closed too, so the corrective has nothing to
 * correct. What a closure looks like on this face is a low aperture that then
 * *releases* — and it is the release, not the closure, that is distinguishable
 * from a mouth simply being shut at rest.
 *
 * **The gate has to be able to reject, not just to downgrade.** Phase 5's trill
 * detector fires roughly once every five seconds in a room with music playing,
 * because music contains genuinely stable amplitude modulation in the 18-36 Hz
 * band — in one recording the music's modulation was both deeper and more stable
 * than the speaker's own trill. No audio threshold separates them. What does is
 * that the speaker's face was not moving. So `mouthActivity` can veto, and a
 * gesture whose face was still is not reported at all.
 *
 * That is a real change of role: vision stops being a refinement and becomes the
 * thing standing between Channel B and a stereo.
 */

import { config } from '../core/config.js';

/**
 * Mouth signals the activity measure watches.
 *
 * Aperture alone is not enough, and a recording proves it. Scored against a
 * marked take, an aperture-only measure vetoed eighteen gestures including the
 * correct trill and the correct whistle — because a whistle is a *held* shape.
 * The lips purse and then stop moving, so aperture's spread over the sound is
 * near zero while `pucker` swings from 0 to 0.6 getting there. A sustained
 * "sss" is the same story with `smile`.
 *
 * lipJitter is deliberately absent: it is a curvature measure aimed at 20-30 Hz
 * flutter that no camera in this project has ever been able to resolve, since
 * Nyquist is 15 Hz at the best frame rate ever recorded here.
 */
const ACTIVITY_SIGNALS = ['aperture', 'width', 'pucker', 'funnel', 'smile', 'stretch'];

/**
 * How much the mouth was doing over a span: the largest standard deviation
 * across the signals above.
 *
 * The question this answers is not "what was the mouth doing" but "was the
 * speaker doing anything at all". A still face during a detected sound is
 * evidence the sound was not theirs.
 *
 * @param {?{sampleAt: (t: number) => any, length: number, at: (i: number) => any}} buffer
 * @param {number} from  performance.now()
 * @param {number} to
 * @returns {?number} null when there is no mouth data covering the span
 */
export function mouthActivity(buffer, from, to) {
  if (!buffer?.length) return null;

  /** @type {Map<string, number[]>} */
  const cols = new Map();
  for (const key of ACTIVITY_SIGNALS) cols.set(key, []);

  let n = 0;
  for (let i = 0; i < buffer.length; i++) {
    const s = buffer.at(i);
    if (s.t < from || s.t > to) continue;
    n++;
    for (const key of ACTIVITY_SIGNALS) {
      if (typeof s[key] === 'number') cols.get(key).push(s[key]);
    }
  }
  // Two samples is not a measure of anything. At 30 Hz a 100 ms span holds
  // three, so a short transient legitimately has no verdict — which is why the
  // caller treats null as "unknown", never as "still".
  if (n < 3) return null;

  let most = 0;
  for (const values of cols.values()) {
    if (values.length < 3) continue;
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    let sum = 0;
    for (const v of values) sum += (v - mean) ** 2;
    const sd = Math.sqrt(sum / values.length);
    if (sd > most) most = sd;
  }
  return most;
}

/**
 * Did the mouth open, right when the burst happened?
 *
 * The spec's `mouthClose` gate replaced with the thing that actually shows on a
 * face. A bilabial closure is silent and looks exactly like a mouth at rest —
 * both are simply shut. What a rest does not have is the release: aperture low
 * before the burst, and climbing across it.
 *
 * @param {?object} buffer  mouth ring buffer
 * @param {number} t  performance.now() of the burst
 * @returns {?boolean} null when there is no mouth data to judge with
 */
export function releaseAt(buffer, t) {
  if (!buffer?.length) return null;
  const v = config.gesture.vision;

  const before = buffer.sampleAt(t - v.RELEASE_LOOKBACK);
  const after = buffer.sampleAt(t + v.RELEASE_WINDOW);
  if (!before || !after) return null;

  // The buffer clamps rather than extrapolating, so a query past the newest
  // sample returns the newest sample. Asking about the future of a burst that
  // has only just happened would compare a sample with itself.
  if (after.t <= before.t) return null;

  return before.aperture <= v.CLOSED_APERTURE && after.aperture - before.aperture >= v.RELEASE_RISE;
}

/**
 * Where in the mouth a trill is being made.
 *
 * Audio gives the rate, vision gives the which — that much of SPEC1.md holds.
 * What does not is the cue: it nominates `lipJitter` elevated plus `mouthClose`
 * mid for bilabial, and neither signal works on a real face at a real frame
 * rate. `lipJitter` separated a trill from silence by a factor of two against a
 * noise floor of the same size, and `mouthClose` never left 0.04.
 *
 * So this uses what does move. A bilabial trill is made with the lips, so the
 * aperture is small and busy. An alveolar one is made with the tongue behind
 * closed-ish teeth, so the mouth is wider and stiller. Anything else is
 * 'unknown', which is a real answer and is returned often — better than three
 * confident labels derived from a signal that is not there.
 *
 * @param {?object} buffer
 * @param {number} from
 * @param {number} to
 * @returns {import('../core/types.js').Place}
 */
export function placeOf(buffer, from, to) {
  const activity = mouthActivity(buffer, from, to);
  if (activity === null) return 'unknown';

  const mid = buffer.sampleAt((from + to) / 2);
  if (!mid) return 'unknown';

  const v = config.gesture.vision;
  if (mid.aperture <= v.CLOSED_APERTURE && activity >= v.BILABIAL_ACTIVITY) return 'bilabial';
  if (mid.stretch >= config.gesture.STRETCH_HIGH) return 'alveolar';
  return 'unknown';
}

/**
 * Which sustained state, if any, one frame looks like.
 *
 * Pure, and deliberately ignorant of duration and history: a frame cannot know
 * how long it has been going on. `GestureDetector` supplies that.
 *
 * Order matters and is the spec's — whistle and growl are narrow and specific,
 * fricative and raspberry are broad and overlap each other. Checking the
 * specific ones first is what stops a whistle being called a fricative on the
 * frames where its harmonics push the zero-crossing rate up.
 *
 * @param {import('../core/types.js').AudioFrame} frame
 * @returns {?import('../core/types.js').GestureType}
 */
export function classifySustained(frame) {
  const g = config.gesture;

  if (
    frame.f0 >= g.whistle.f0Lo &&
    frame.f0 <= g.whistle.f0Hi &&
    frame.periodicity >= g.whistle.minPeriodicity &&
    frame.flatness <= g.whistle.maxFlatness
  ) {
    return 'whistle';
  }

  if (
    frame.f0 > 0 &&
    frame.f0 <= g.growl.f0Max &&
    frame.periodicity <= g.growl.maxPeriodicity &&
    frame.rms >= g.growl.minRms &&
    // Not in SPEC1.md's rule, and a sustained "sss" is classified as a growl
    // without it. Autocorrelation on broadband noise returns some low frequency
    // with low periodicity, which is the growl condition exactly.
    frame.flatness <= g.growl.maxFlatness
  ) {
    return 'growl';
  }

  if (frame.zcr >= g.fricative.minZcr && frame.flatness >= g.fricative.minFlatness) {
    return 'fricative';
  }

  // `f0 absent` in the spec's table. A raspberry is loud, broadband and has no
  // pitch worth the name — which is also a fair description of a fricative,
  // hence the spec's own warning that this is the confusion pair to expect.
  // Flatness is the only thing that leans, so the raspberry threshold sits
  // above the fricative one and this check comes second.
  if (frame.flatness >= g.raspberry.minFlatness && frame.periodicity < g.whistle.minPeriodicity) {
    return 'raspberry';
  }

  return null;
}

/**
 * The classifier proper: frames in, gestures out.
 *
 * Two shapes of sound, handled differently because they are different:
 *
 * **Transients** (plosive, click) exist in one frame. There is nothing to
 * accumulate and nothing to wait for except the tail, which is what separates
 * the two — a click is over almost immediately, a plosive has a little more
 * behind it.
 *
 * **Sustained states** (whistle, growl, fricative, raspberry) have to hold for
 * `HYSTERESIS_MS` before they are believed and are emitted when they end, with
 * a real duration. A state that never reaches its minimum duration is discarded
 * rather than emitted short.
 *
 * **Trills** arrive already formed from `EnvelopeAnalyser`, and pass through
 * `trill()` only to be gated by vision and given a place.
 */
export class GestureDetector {
  /**
   * @param {object} o
   * @param {?object} [o.mouthBuffer]  vision gate; without one nothing is vetoed
   * @param {(e: import('../core/types.js').GestureEvent) => void} [o.onGesture]
   */
  constructor({ mouthBuffer = null, onGesture } = {}) {
    this.mouthBuffer = mouthBuffer;
    this.onGesture = onGesture ?? (() => {});
    /** The sustained state currently accumulating. */
    this.active = null;
    /** A transient waiting to see how long its tail is. */
    this.pending = null;
    /** Rejected-by-vision count, so the UI can say how often it fires. */
    this.vetoed = 0;
  }

  /**
   * Fold in one rAF-rate feature frame.
   *
   * @param {import('../core/types.js').AudioFrame} frame
   * @returns {?import('../core/types.js').GestureEvent}
   */
  push(frame) {
    const g = config.gesture;

    // A transient in flight takes priority: it is deciding between click and
    // plosive on the basis of what happens next, and what happens next is this.
    if (this.pending) {
      const emitted = this._advanceTransient(frame);
      if (emitted) return emitted;
    }

    // Transients first. A burst is loud and brief and would otherwise be
    // swallowed by whichever sustained state its spectrum happens to match.
    if (!this.pending && frame.attack >= g.plosive.attackDb) {
      // Any sustained state in progress ends here — a burst is a new event, not
      // a continuation of the hiss before it.
      const ended = this._endSustained();
      this.pending = { from: frame.t, peakAttack: frame.attack, flatness: frame.flatness };
      return ended;
    }

    const type = classifySustained(frame);
    if (type && this.active?.type === type) {
      this.active.to = frame.t;
      this.active.frames++;
      this.active.zcr = Math.max(this.active.zcr, frame.zcr);
      this.active.rms = Math.max(this.active.rms, frame.rms);
      return null;
    }

    const ended = this._endSustained();
    if (type) {
      this.active = {
        type,
        from: frame.t,
        to: frame.t,
        frames: 1,
        zcr: frame.zcr,
        rms: frame.rms,
      };
    }
    return ended;
  }

  /**
   * A transient is resolved by its tail: a click has essentially none, a
   * plosive has a little. Both are decided within `plosive.maxDur`, so this
   * never holds anything for long.
   */
  _advanceTransient(frame) {
    const g = config.gesture;
    const dur = frame.t - this.pending.from;

    this.pending.peakAttack = Math.max(this.pending.peakAttack, frame.attack);
    this.pending.flatness = Math.max(this.pending.flatness, frame.flatness);

    const quiet = frame.attack < g.plosive.attackDb / 2;
    if (!quiet && dur < g.plosive.maxDur) return null;

    const p = this.pending;
    this.pending = null;

    // Click against plosive: the spec separates them by duration and a low tail,
    // and duration is the part that can be measured without lookahead. The
    // higher attack threshold is a supporting condition rather than the test,
    // because every click also clears the plosive threshold.
    const type = dur <= g.click.maxDur && p.peakAttack >= g.click.attackDb ? 'click' : 'plosive';

    // A plosive wants spectral evidence too. A burst with no broadband content
    // is a thump on the desk, not a consonant.
    if (type === 'plosive' && p.flatness < g.plosive.minFlatness) return null;

    return this._emit({
      t: p.from,
      type,
      dur: Math.max(dur, 1),
      intensity: Math.min(1, p.peakAttack / 40),
      detectLatency: config.latency[type],
    });
  }

  /**
   * Close the sustained state, if it earned its minimum duration.
   *
   * The end time comes from the last frame that still matched, not from now:
   * the state stopped when it stopped being true, and a duration measured to
   * the moment somebody noticed would grow by however long the gap was.
   */
  _endSustained() {
    if (!this.active) return null;
    const a = this.active;
    this.active = null;

    const g = config.gesture;
    const dur = a.to - a.from;
    if (dur < g.HYSTERESIS_MS) return null;

    const minDur = g[a.type]?.minDur ?? 0;
    if (dur < minDur) return null;

    return this._emit({
      t: a.from,
      type: a.type,
      dur,
      intensity: Math.min(1, a.type === 'fricative' ? a.zcr : a.rms * 10),
      detectLatency: config.latency[a.type],
    });
  }

  /**
   * A trill from the envelope analyser, gated and placed.
   *
   * The only detector whose events do not originate here, and the one that most
   * needs the veto: without it, a room with music produces a trill every few
   * seconds.
   *
   * @param {import('../core/types.js').GestureEvent} event
   * @returns {?import('../core/types.js').GestureEvent}
   */
  trill(event) {
    return this._emit({
      ...event,
      place: placeOf(this.mouthBuffer, event.t, event.t + event.dur),
    });
  }

  /**
   * Apply the vision gate and hand the event on.
   *
   * Three outcomes, and the middle one is the important one:
   *
   *   face was moving      confidence 1.0
   *   face was still       vetoed — not reported at all
   *   no mouth data        confidence 0.6, reported
   *
   * "No camera" must not mean "no gesture", or Channel B would stop working
   * whenever the camera was off — which is a legitimate way to use this. But a
   * face that is demonstrably still while a sound is detected is positive
   * evidence that the sound came from somewhere else.
   */
  _emit(event) {
    const g = config.gesture;

    // Judged over a neighbourhood, not over the gesture's own span. A click is
    // fifteen milliseconds long and a camera delivers a frame every thirty-three
    // — there is nothing inside it to look at. The question the gate asks is
    // whether the speaker was doing anything around then, which is a question
    // about a few hundred milliseconds either way.
    const half = Math.max(g.vision.ACTIVITY_WINDOW, event.dur) / 2;
    const mid = event.t + event.dur / 2;
    const activity = mouthActivity(this.mouthBuffer, mid - half, mid + half);

    let confidence = g.CONF_WITHOUT_VISION;
    if (activity !== null) {
      if (activity < g.vision.ACTIVITY_MIN) {
        this.vetoed++;
        return null;
      }
      confidence = g.CONF_WITH_VISION;
    }

    // A plosive additionally wants its release: aperture low before the burst
    // and climbing across it. Absent, it is still reported — plenty of plosives
    // are not bilabial and never close the lips at all — but with less
    // confidence, which is what the spec asks for and why it is not a veto.
    //
    // Unless you ask for it to be. The detector fires on the stressed syllable
    // of ordinary speech, and on one take four of roughly sixty plosives had a
    // release behind them: the switch is the difference between sixty marks and
    // four, and which of those is right is a thing you can hear in ten seconds
    // and cannot settle by reading a recording. `false` only — a null means
    // there was no camera to ask, and that must never veto anything.
    if (event.type === 'plosive' && releaseAt(this.mouthBuffer, event.t) === false) {
      if (g.vision.RELEASE_REQUIRED) {
        this.vetoed++;
        return null;
      }
      confidence = Math.min(confidence, g.CONF_WITHOUT_VISION);
    }

    const out = { place: 'unknown', ...event, confidence };
    this.onGesture(out);
    return out;
  }

  /** Close anything in progress. Call when the microphone stops. */
  flush() {
    // A transient that never resolved is not evidence of anything.
    this.pending = null;
    return this._endSustained();
  }

  reset() {
    this.active = null;
    this.pending = null;
    this.vetoed = 0;
  }
}

export default GestureDetector;
